'use client';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface AppUser {
  phone: string;
  name: string;
  role: 'user' | 'seller' | 'admin';
  joinedAt: string;
}

export interface AppState {
  user: AppUser | null;
  credits: number;
  walletBalance: number;
  postCount: number; // number of products posted (first is free)
}

// ─── Storage Keys ─────────────────────────────────────────────────────────────
const KEYS = {
  USER: 'mkubwa_user',
  CREDITS: 'mkubwa_credits',
  WALLET: 'mkubwa_wallet',
  POST_COUNT: 'mkubwa_post_count',
} as const;

// ─── Defaults ─────────────────────────────────────────────────────────────────
const DEFAULTS: AppState = {
  user: null,
  credits: 12,
  walletBalance: 3450,
  postCount: 0,
};

// ─── Safe localStorage helpers ────────────────────────────────────────────────
function safeGet<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeSet(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

// ─── Event emitter for cross-component sync ───────────────────────────────────
type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach(fn => fn());
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── Read current state ───────────────────────────────────────────────────────
export function getState(): AppState {
  return {
    user: safeGet<AppUser | null>(KEYS.USER, DEFAULTS.user),
    credits: safeGet<number>(KEYS.CREDITS, DEFAULTS.credits),
    walletBalance: safeGet<number>(KEYS.WALLET, DEFAULTS.walletBalance),
    postCount: safeGet<number>(KEYS.POST_COUNT, DEFAULTS.postCount),
  };
}

// ─── Actions ──────────────────────────────────────────────────────────────────
export function login(user: AppUser): void {
  safeSet(KEYS.USER, user);
  notify();
}

export function logout(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(KEYS.USER);
    localStorage.removeItem('mkubwa_device_trusted');
  }
  notify();
}

export function addCredits(amount: number): void {
  const current = safeGet<number>(KEYS.CREDITS, DEFAULTS.credits);
  safeSet(KEYS.CREDITS, current + amount);
  notify();
}

export function spendCredits(amount: number): boolean {
  const current = safeGet<number>(KEYS.CREDITS, DEFAULTS.credits);
  if (current < amount) return false;
  safeSet(KEYS.CREDITS, current - amount);
  notify();
  return true;
}

export function addWalletBalance(amount: number): void {
  const current = safeGet<number>(KEYS.WALLET, DEFAULTS.walletBalance);
  safeSet(KEYS.WALLET, current + amount);
  notify();
}

export function incrementPostCount(): void {
  const current = safeGet<number>(KEYS.POST_COUNT, DEFAULTS.postCount);
  safeSet(KEYS.POST_COUNT, current + 1);
  notify();
}

/** Returns cost for next post: 0 for first post, 10 for all subsequent */
export function getNextPostCost(): number {
  const count = safeGet<number>(KEYS.POST_COUNT, DEFAULTS.postCount);
  return count === 0 ? 0 : 10;
}
